// @ts-nocheck
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { useVideoValidation } from 'hooks/playback';
import {
  useVideoById,
  useCurrentVideoSetter,
  useFallbackVideo,
} from 'hooks/video';

import { networkDisconnected } from 'actions/network';
import { exitFullScreenPlayer } from 'actions/hero';

const getSecondsToStart = video => {
  return video
    ? video.schedule.videoStartDate - Math.floor(Date.now() / 1000)
    : null;
};

const useGameTransitionVideo = video => {
  const preGameVideo = video?.event.pregame;
  const [postGameVideo] = useVideoById(video?.event.postEvent);

  let gameTransitionVideo = video;
  let videoToDisplay = video;
  if (video) {
    if (video.event.status === 'pre' && getSecondsToStart(video) > 0) {
      gameTransitionVideo = preGameVideo;
    }
    if (video.event.status === 'post') {
      gameTransitionVideo = postGameVideo;
      videoToDisplay = postGameVideo;
    }
  }

  const [override, setOverride] = useState(null);
  useEffect(() => {
    if (!video || video.event.status !== 'pre') {
      return undefined;
    }

    const secondsToStart = getSecondsToStart(video);
    // Kick off timer to transition user to the live event
    const timeout = setTimeout(() => {
      setOverride(video);
    }, Math.max(0, secondsToStart * 1000));
    return () => {
      setOverride(null);
      clearTimeout(timeout);
    };
  }, [video]);

  const prevVideo = useRef(video);
  useEffect(() => {
    prevVideo.current = video;
  }, [video]);
  const isAutoplay = useMemo(() => {
    // We're only autoplaying (w/ game transitions) if we're watching the same video
    if (video && prevVideo.current?.id === video.id) {
      // and the video status had updated
      if (prevVideo.current.event.status !== video.event.status) {
        return true;
      }
      // or if we set the override
      if (override !== null) {
        return true;
      }
    }

    return false;
  }, [video, override]);

  return {
    videoToPlay: override || gameTransitionVideo,
    videoToDisplay,
    isAutoplay,
  };
};

const usePlayableVideo = (desiredVideo, desiredRow, onError, isCarousel) => {
  const dispatch = useDispatch();
  const lastPlayable = useRef({ video: null, row: null });
  const [currentIds, setCurrentIds] = useState({ video: null, row: null });

  const [currentVideo, currentRow] = useVideoById(
    currentIds.video,
    currentIds.row,
  );

  const [fallbackVideo, fallbackRow] = useFallbackVideo();

  // Falling back to current video as don't want to trigger changes to
  // isValid or authorizedProvider by passing in null to useVideoValidation
  // since the user should still be watching their previously selected stream
  // even after navigating to a page with no video in the query parameters
  const videoToValidate =
    desiredVideo || currentVideo || lastPlayable.current.video;
  const rowToValidate = desiredRow || currentRow || lastPlayable.current.row;

  const handleError = error => {
    if (error.type === 'authN') {
      if (isCarousel) {
        dispatch(exitFullScreenPlayer());
      }
      if (currentVideo?.id === videoToValidate.id) {
        setCurrentIds({ video: fallbackVideo.id, row: fallbackRow.id });
        return;
      }
    }
    onError(error);
  };

  // Don't need to revalidate if we're attempting to play the currently playing video
  const { isValid, authorizedProvider, loading } = useVideoValidation(
    videoToValidate,
    handleError,
    isCarousel,
  );

  let playableVideo = null;
  let playableRow = null;

  if (isValid && !loading) {
    playableVideo = videoToValidate;
    playableRow = rowToValidate;
  } else if (currentVideo) {
    playableVideo = currentVideo;
    playableRow = currentRow;
  }

  if (!playableVideo) {
    // Fallback for no video
    if (fallbackVideo && !isCarousel) {
      playableVideo = fallbackVideo;
      playableRow = fallbackRow;
    }
  }

  useEffect(() => {
    setCurrentIds({ video: playableVideo?.id, row: playableRow?.id });
    lastPlayable.current = { video: playableVideo, row: playableRow };
  }, [playableVideo, playableRow]);

  return {
    playableVideo,
    playableRow,
    authorizedProvider,
    loading,
  };
};

const PlayerFunnel = ({
  videoId,
  rowId,
  render,
  onError,
  isCarousel,
  isDiscoveryRowVideo,
}) => {
  const dispatch = useDispatch();
  const [video, row] = useVideoById(videoId, rowId, isDiscoveryRowVideo);

  const {
    playableVideo,
    playableRow,
    authorizedProvider,
    loading,
  } = usePlayableVideo(video, row, onError, isCarousel);

  const {
    videoToPlay,
    videoToDisplay,
    isAutoplay: isGTAutoplay,
  } = useGameTransitionVideo(playableVideo);

  const setCurrentVideo = useCurrentVideoSetter();
  const onSetResource = useCallback(() => {
    setCurrentVideo(
      videoToDisplay,
      videoToDisplay?.id,
      playableRow?.id,
      isDiscoveryRowVideo,
    );
  }, [videoToDisplay, playableRow, setCurrentVideo, isDiscoveryRowVideo]);

  const onPlaybackError = error => {
    if (error.detail.error.code === '3002') {
      dispatch(networkDisconnected());
      onError({ type: 'network', error });
    } else {
      onError({ type: 'playback', error });
    }
  };

  return render({
    videoToPlay,
    videoToDisplay,
    isGTAutoplay,
    row: playableRow,
    authorizedProvider,
    loading,
    onPlaybackError,
    onSetResource,
  });
};

PlayerFunnel.propTypes = {
  videoId: PropTypes.string,
  rowId: PropTypes.string,
  render: PropTypes.func,
  onError: PropTypes.func,
  isCarousel: PropTypes.bool,
};

PlayerFunnel.defaultProps = {
  videoId: null,
  rowId: null,
  render: () => null,
  onError: () => null,
  isCarousel: false,
};

export default PlayerFunnel;
